import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Activity,
  GitBranch,
  Link2,
  Settings,
  Share,
  Download,
  ExternalLink,
  Eye,
  Maximize2,
  Minimize2,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useMediaRecord } from '../hooks/useMediaRecord';
import { usePrefetchMediaRecord } from '../hooks/useMediaRecord';
import { useAnalyticsTracker } from '../hooks/useMediaAnalytics';
import RecordDetails from '../components/media-record/RecordDetails';
import RecordAnalytics from '../components/media-record/RecordAnalytics';
import VersionHistory from '../components/media-record/VersionHistory';
import RelatedRecords from '../components/media-record/RelatedRecords';
import MetadataEditor from '../components/media-record/MetadataEditor';
import AIMetadataExtraction from '../components/media-record/AIMetadataExtraction';

type TabType = 'overview' | 'analytics' | 'versions' | 'relationships' | 'metadata' | 'ai-extraction';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<{ mediaId: string; className?: string }>;
}

const tabs: TabConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Eye,
    component: RecordDetails
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: Activity,
    component: RecordAnalytics
  },
  {
    id: 'versions',
    label: 'Versions',
    icon: GitBranch,
    component: VersionHistory
  },
  {
    id: 'relationships',
    label: 'Relationships',
    icon: Link2,
    component: RelatedRecords
  },
  {
    id: 'metadata',
    label: 'Metadata',
    icon: Settings,
    component: MetadataEditor
  },
  {
    id: 'ai-extraction',
    label: 'AI Extraction',
    icon: Sparkles,
    component: AIMetadataExtraction
  }
];

interface MediaRecordPageProps {
  mediaId?: string; // Optional for direct usage
}

const MediaRecordPage: React.FC<MediaRecordPageProps> = ({ mediaId: propMediaId }) => {
  const { mediaId: paramMediaId } = useParams<{ mediaId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use prop mediaId if provided, otherwise use URL param
  const mediaId = propMediaId || paramMediaId;
  
  // Get active tab from URL params or default to overview
  const activeTab = (searchParams.get('tab') as TabType) || 'overview';
  const isFullscreen = searchParams.get('fullscreen') === 'true';

  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Hooks
  const { data: record, isLoading, error, refetch } = useMediaRecord(mediaId || '');
  const { prefetchMediaRecord } = usePrefetchMediaRecord();
  const { trackView, sessionId } = useAnalyticsTracker(mediaId || '');

  // Track page view on mount and mediaId change
  useEffect(() => {
    if (mediaId) {
      trackView();
    }
  }, [mediaId]); // Remove trackView dependency to prevent infinite loop

  // Prefetch related data
  useEffect(() => {
    if (mediaId) {
      // Prefetch other potential media records that might be related
      // This could be enhanced with actual related record IDs
    }
  }, [mediaId, prefetchMediaRecord]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Handle browser navigation if needed
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (!mediaId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center">
        <div className="cosmic-glass rounded-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Invalid Media ID</h1>
          <p className="text-gray-400 mb-4">No media ID provided in the URL</p>
          <button
            onClick={() => navigate('/media')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Go to Media Library
          </button>
        </div>
      </div>
    );
  }

  const setActiveTab = (tab: TabType) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tab);
    }
    setSearchParams(newSearchParams);
  };

  const toggleFullscreen = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (isFullscreen) {
      newSearchParams.delete('fullscreen');
    } else {
      newSearchParams.set('fullscreen', 'true');
    }
    setSearchParams(newSearchParams);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: record?.title || 'Media Record',
        text: record?.description || 'Check out this media record',
        url: window.location.href
      });
    } catch (error) {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
      // Could show a toast here
    }
  };

  const handleDownload = () => {
    if (record) {
      // Implement download logic
      console.log('Download media:', record);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/media');
    }
  };

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || RecordDetails;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading media record...</p>
          <p className="text-gray-400 text-sm mt-2">Session: {sessionId?.substring(0, 8)}...</p>
        </div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center">
        <div className="cosmic-glass rounded-lg p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-white mb-2">Failed to Load Media Record</h1>
          <p className="text-gray-400 mb-4">
            {error?.message || 'The requested media record could not be found.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Retry
            </button>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 ${
      isFullscreen ? 'fixed inset-0 z-50' : ''
    }`}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="cosmic-glass rounded-lg p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!isFullscreen && (
                <button
                  onClick={handleGoBack}
                  className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                  title="Go back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-white truncate">
                  {record.title}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-400">ID: {mediaId}</span>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-sm text-gray-400 capitalize">{record.media_type}</span>
                  <span className="text-sm text-gray-400">•</span>
                  <span className="text-sm text-gray-400">{record.format}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={handleShare}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                title="Share"
              >
                <Share className="w-5 h-5" />
              </button>

              <button
                onClick={handleDownload}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>

              <button
                onClick={() => window.open(`/media/${mediaId}`, '_blank')}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-5 h-5" />
              </button>

              <button
                onClick={toggleFullscreen}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 mt-6 overflow-x-auto">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <ActiveComponent 
            mediaId={mediaId} 
            className={isFullscreen ? 'max-w-none' : 'max-w-none'} 
          />
        </motion.div>

        {/* Footer */}
        {!isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="text-center text-gray-400 text-sm"
          >
            <p>
              Last updated: {new Date(record.updated_at).toLocaleString()}
              {record.created_by && (
                <span> • Created by: {record.created_by.name}</span>
              )}
            </p>
            {sessionId && (
              <p className="mt-1 text-xs">
                Analytics Session: {sessionId}
              </p>
            )}
          </motion.div>
        )}
      </div>

      {/* Fullscreen overlay background */}
      {isFullscreen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
        />
      )}
    </div>
  );
};

export default MediaRecordPage;